/**
 * Where in the Woods — late-night woods stage. Smaller, lower-key, more
 * organic. Surrounded by string lights (added in props/Vegetation pass).
 */

import * as THREE from 'three';
import { type StageHandle, makeScreenQuad } from './StageBase';

export class WoodsStage {
  static build(): StageHandle {
    const group = new THREE.Group();
    group.name = 'WoodsStage';

    const matWood = new THREE.MeshStandardMaterial({
      color: '#3a2818',
      roughness: 0.85
    });
    const matBark = new THREE.MeshStandardMaterial({
      color: '#2a1c10',
      roughness: 0.9
    });
    const matCanopy = new THREE.MeshStandardMaterial({
      color: '#3a4a28',
      roughness: 0.85,
      side: THREE.DoubleSide
    });
    const matScreen = new THREE.MeshBasicMaterial({ color: '#0a0a14' });
    const matGlow = new THREE.MeshStandardMaterial({
      color: '#ffcb6b',
      emissive: '#ffcb6b',
      emissiveIntensity: 1.6
    });

    const w = 22;
    const d = 16;
    const deckH = 1.3;

    // Wooden deck
    const deck = new THREE.Mesh(new THREE.BoxGeometry(w, deckH, d), matWood);
    deck.position.y = deckH / 2;
    deck.castShadow = true;
    deck.receiveShadow = true;
    group.add(deck);

    // Tree-trunk corner posts
    const postH = 6;
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.3, 0.4, postH, 8),
          matBark
        );
        post.position.set(sx * w / 2, postH / 2 + deckH, sz * d / 2);
        post.castShadow = true;
        group.add(post);
      }
    }

    // Leafy canopy roof — tilted plane
    const canopy = new THREE.Mesh(
      new THREE.PlaneGeometry(w + 2, d + 2),
      matCanopy
    );
    canopy.rotation.x = -Math.PI / 2;
    canopy.position.y = postH + deckH + 0.2;
    group.add(canopy);

    // Small backdrop screen
    const screenW = 6;
    const screenH = 3.4;
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(screenW, screenH),
      matScreen
    );
    screen.position.set(0, deckH + screenH / 2 + 0.6, -d / 2 + 0.3);
    screen.rotation.y = Math.PI;
    group.add(screen);

    // Glowing lanterns
    for (let i = 0; i < 6; i++) {
      const t = i / 6;
      const lantern = new THREE.Mesh(
        new THREE.SphereGeometry(0.18, 8, 6),
        matGlow
      );
      lantern.position.set(-w / 2 + t * w, deckH + 4.6, d / 2 - 0.2);
      group.add(lantern);
    }

    const audioAnchor = new THREE.Object3D();
    audioAnchor.position.set(0, 2, 0);
    group.add(audioAnchor);

    return {
      id: 'woods',
      label: 'Where in the Woods',
      group,
      position: new THREE.Vector3(),
      audioAnchor,
      audioFalloff: { ref: 28, max: 160 },
      screenQuad: makeScreenQuad(
        new THREE.Vector3(0, deckH + screenH / 2 + 0.6, -d / 2 + 0.3),
        screenW,
        screenH,
        Math.PI
      ),
      screenWidth: screenW,
      screenHeight: screenH
    };
  }
}
